import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { first, map, mergeMap, switchMap } from 'rxjs/operators';
import { QuestionSectionStateDto } from '../../model/exam/state/question-section-state-dto';
import { CandidateQuestionEntity } from '../entities/candidate-question-entity';
import { QuestionSectionEntity } from '../entities/question-section-entity';
import { CandidateQuestionRepositoryService } from '../repositories/candidate-question-repository.service';
import { QuestionSectionRepositoryService } from '../repositories/question-section-repository.service';
import { QuestionSectionDto } from '../../model/exam/question-section-dto';
import { KeyValueRepositoryService } from '../repositories/key-value-repository.service';

@Injectable({
	providedIn: 'root'
})
export class QuestionSectionDataService {
	constructor(private candidateQuestionRepository: CandidateQuestionRepositoryService,
		private questionSectionRepository: QuestionSectionRepositoryService,
		private keyValueDataRepository: KeyValueRepositoryService) {
		this.getSectionQuestions = this.getSectionQuestions.bind(this);
	}

	public get(key: string): Observable<QuestionSectionDto> {
		return this.questionSectionRepository.get('questionSectionId', key)
			.pipe(map((data: QuestionSectionEntity) => {
				return this.questionSectionRepository.convertDto(data);
			}));
	}

	public getAll(): Observable<QuestionSectionDto[]> {
		return this.questionSectionRepository.getAll()
			.pipe(map(sectionEntities => sectionEntities.map(sectionEntity => this.questionSectionRepository.convertDto(sectionEntity))));
	}

	public put(section: QuestionSectionDto): Observable<void> {
		const entity: QuestionSectionEntity = this.questionSectionRepository.convertEntity(section);
		return this.questionSectionRepository.put(entity);
	}

	public getBySectionNumber(sectionNumber: number) {
		return this.questionSectionRepository.getBySectionNumber(sectionNumber)
			.pipe(map((data: QuestionSectionEntity) => {
				return this.questionSectionRepository.convertDto(data);
			}));
	}

	public getCurrentSection(): Observable<QuestionSectionDto> {
		return this.keyValueDataRepository.getCurrentSectionNumber()
			.pipe(mergeMap((currentSectionNumber: number) => this.getBySectionNumber(currentSectionNumber)))
	}

	/**
	 * Gives the order index of the earliest section that the candidate is permitted to open.
	 *
	 * A paper that times each section on its own stops at the section that the timer runs,
	 * because the clock of an earlier section has already finished. A paper that times the
	 * complete assessment with one clock, and that permits a revisit, opens each section the
	 * candidate has reached - so the first section of the paper is the limit.
	 */
	public getFirstReachableSectionNumber(): Observable<number> {
		return forkJoin({
			currentSectionNumber: this.keyValueDataRepository.getCurrentSectionNumber().pipe(first()),
			canRevisitSections: this.getCanRevisitSections().pipe(first()),
			sections: this.getAll().pipe(first())
		})
		.pipe(map((data: { currentSectionNumber: number, canRevisitSections: boolean, sections: QuestionSectionDto[] }) => {
			if (!data.canRevisitSections || !data.sections.length) {
				return data.currentSectionNumber;
			}

			return Math.min(...data.sections.map((section: QuestionSectionDto) => section.orderIndex));
		}));
	}

	/**
	 * Tells if the candidate can come back to a section after leaving it. The option belongs to
	 * the paper, so the answer is the same for each of its sections. It needs one clock for the
	 * complete exam, because the clock of a section that is complete has already finished.
	 */
	public getCanRevisitSections(): Observable<boolean> {
		return forkJoin({
			excludeSectionDuration: this.keyValueDataRepository.getExcludeSectionDuration().pipe(first()),
			canRevisitSections: this.keyValueDataRepository.getCanRevisitSections().pipe(first())
		})
		.pipe(map((data: { excludeSectionDuration: boolean, canRevisitSections: boolean }) =>
			data.excludeSectionDuration && data.canRevisitSections));
	}

	public getNextSection(sectionNumber: number): Observable<QuestionSectionDto> {
		return this.questionSectionRepository.getBySectionNumber(++sectionNumber)
			.pipe(map((data: QuestionSectionEntity) => {

					return this.questionSectionRepository.convertDto(data);
			}));
	}

	public getSectionQuestions(): Observable<QuestionSectionStateDto[]> {
		return this.questionSectionRepository.getAll()
			.pipe(switchMap((entities: QuestionSectionEntity[]) => forkJoin(entities.map((sectionEntity: QuestionSectionEntity) => {
				return forkJoin({
					section: of(sectionEntity),
					questionStates: this.candidateQuestionRepository.getSectionQuestions(sectionEntity.questionSectionId)
				}).pipe(map((data: { section: QuestionSectionEntity, questionStates: CandidateQuestionEntity[] }) => {
					const sectionState: QuestionSectionStateDto = {
						...this.questionSectionRepository.convertDto(data.section),
						questionStates: data.questionStates.map(candidateQuestionEntity => {
							return {
								question: this.candidateQuestionRepository.convertDto(candidateQuestionEntity),
								layoutItems: []
							};
						})
					};
					return sectionState;
				}));
			}))));
	}

	public updateTimeAdjustment(section: QuestionSectionDto): Observable<void> {
		return this.get(section.id)
			.pipe(map((localSection: QuestionSectionDto) => {
				localSection.timeAdjustment = (localSection.timeAdjustment ? localSection.timeAdjustment : 0) + section.timeAdjustment!;
				return localSection;
			}))
			.pipe(mergeMap((localSection: QuestionSectionDto) => this.put(localSection)));
	}
}
